import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Play, Image, Video } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getPreviousEvents, getGalleryImages, getGalleryVideos } from '../api/client';
import { cardImage, thumbnailImage } from '../utils/imageOptimizer';

// Fallback placeholder for broken images
const FALLBACK_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiB2aWV3Qm94PSIwIDAgNDAwIDMwMCI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiMxZjI5MzciLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZiNzI4MCIgZm9udC1zaXplPSIxNCI+SW1hZ2Ugbm90IGF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';

// Image component with error handling
const GalleryImage = ({ src, alt, className, onClick, onError }) => {
  const [imgError, setImgError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const handleError = () => {
    setImgError(true);
    setLoading(false);
    if (onError) onError();
  };

  const handleLoad = () => {
    setLoading(false);
  };

  return (
    <div className={`relative ${className}`}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="animate-spin h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      )}
      <img
        src={imgError ? FALLBACK_IMAGE : cardImage(src)}
        alt={alt}
        className={`w-full h-full object-cover ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onClick={onClick}
        onError={handleError}
        onLoad={handleLoad}
      />
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ video, onClose }) => {
  if (!video) return null;

  const getVideoSrc = () => {
    if (video.type === 'youtube') {
      const videoId = video.url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/)?.[1];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    } else if (video.type === 'vimeo') {
      const videoId = video.url.match(/vimeo\.com\/(\d+)/)?.[1];
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    return video.url;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full z-10"
      >
        <X size={32} />
      </button>
      
      <div 
        className="w-full max-w-4xl aspect-video"
        onClick={(e) => e.stopPropagation()}
      >
        {video.type === 'mp4' || video.type === 'upload' ? (
          <video
            src={video.url}
            controls
            autoPlay
            className="w-full h-full rounded-lg"
          />
        ) : (
          <iframe
            src={getVideoSrc()}
            title={video.title}
            className="w-full h-full rounded-lg"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
      
      <div className="absolute bottom-4 left-0 right-0 text-center text-white">
        <p className="text-lg font-medium">{video.title}</p>
        {video.description && <p className="text-sm text-gray-400">{video.description}</p>}
      </div>
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, onClick }) => {
  const getThumbnail = () => {
    if (video.thumbnail) return video.thumbnail;
    if (video.type === 'youtube') {
      const videoId = video.url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/)?.[1];
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  const thumbnail = getThumbnail();

  return (
    <div 
      className="relative group cursor-pointer rounded-xl overflow-hidden bg-gray-800"
      onClick={onClick}
    >
      <div className="aspect-video">
        {thumbnail ? (
          <img 
            src={thumbnail} 
            alt={video.title} 
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gray-700 flex items-center justify-center">
            <Video size={48} className="text-gray-500" />
          </div>
        )}
      </div>
      
      {/* Play Button Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors">
        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center transform group-hover:scale-110 transition-transform">
          <Play size={28} className="text-gray-900 ml-1" fill="currentColor" />
        </div>
      </div>
      
      {/* Title Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-white font-medium truncate">{video.title}</p>
      </div>
    </div>
  );
};

export const GallerySection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const [activeTab, setActiveTab] = useState('photos');
  const [events, setEvents] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [videoModal, setVideoModal] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const primaryColor = settings.colors?.primary || '#0E7A5F';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsData, imagesData] = await Promise.all([
        getPreviousEvents(),
        getGalleryImages()
      ]);
      
      // Try to load videos
      let videosData = { videos: [] };
      try {
        videosData = await getGalleryVideos();
      } catch {
        // Videos API may not be available
      }
      
      setEvents(eventsData.events || []);
      setGalleryImages(imagesData.images || []);
      setGalleryVideos(videosData.videos || []);
    } catch {
      setEvents([]);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-slide every 5 seconds for photos
  useEffect(() => {
    if (activeTab !== 'photos' || galleryImages.length === 0) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [galleryImages.length, activeTab]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const sectionTitle = getBilingualValue(settings.gallery?.title) || t('gallery.title');
  const sectionDesc = getBilingualValue(settings.gallery?.description) || t('gallery.description');

  if (loading) {
    return (
      <section id="gallery" className="py-20 bg-gray-900">
        <div className="container mx-auto px-6 flex justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full"></div>
        </div>
      </section>
    );
  }

  // Hide entire section if no content
  if (events.length === 0 && galleryImages.length === 0 && galleryVideos.length === 0) {
    return null;
  }

  return (
    <section id="gallery" className="py-20 bg-gray-900">
      <div className="container mx-auto px-6">
        {/* Previous Events Stats */}
        <h2 className="text-4xl font-bold text-white text-center mb-4">
          {sectionTitle}
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          {sectionDesc}
        </p>

        {/* Previous Events Timeline */}
        {events.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {events.map((event, idx) => (
              <div 
                key={event.id || idx} 
                className="relative bg-gray-800 rounded-xl overflow-hidden hover:bg-gray-700 transition-colors group"
              >
                {event.image && (
                  <div 
                    className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity"
                    style={{ 
                      backgroundImage: `url(${event.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                )}
                
                <div className="relative p-6 text-center">
                  <div 
                    className="text-3xl font-bold mb-2"
                    style={{ color: primaryColor }}
                  >
                    {event.year}
                  </div>
                  <div className="text-white font-semibold mb-1">{event.location}</div>
                  <div className="text-gray-400 text-sm mb-4 line-clamp-2">{event.title}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-900/80 rounded p-2">
                      <div className="text-white font-bold">{event.countries}+</div>
                      <div className="text-gray-500">{t('stats.countries')}</div>
                    </div>
                    <div className="bg-gray-900/80 rounded p-2">
                      <div className="text-white font-bold">{event.attendees}+</div>
                      <div className="text-gray-500">{t('stats.attendees')}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Gallery Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'photos'
                ? 'text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            style={activeTab === 'photos' ? { backgroundColor: primaryColor } : {}}
          >
            <Image size={20} />
            {t('gallery.photos')}
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
              {galleryImages.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'videos'
                ? 'text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            style={activeTab === 'videos' ? { backgroundColor: primaryColor } : {}}
          >
            <Video size={20} />
            {t('gallery.videos')}
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
              {galleryVideos.length}
            </span>
          </button>
        </div>

        {/* Photos Tab Content */}
        {activeTab === 'photos' && galleryImages.length > 0 && (
          <>
            <div className="relative max-w-4xl mx-auto px-2 sm:px-0">
              {/* Main Slideshow */}
              <div className="relative aspect-[4/3] sm:aspect-[16/10] rounded-xl sm:rounded-2xl overflow-hidden bg-gray-800">
                <GalleryImage
                  src={galleryImages[currentIndex]?.src}
                  alt={galleryImages[currentIndex]?.alt || 'Gallery'}
                  className="w-full h-full cursor-pointer"
                  onClick={() => openLightbox(currentIndex)}
                />
                
                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 sm:p-6">
                  <p className="text-white text-sm sm:text-lg font-medium line-clamp-2">
                    {galleryImages[currentIndex]?.caption || ''}
                  </p>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                >
                  <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                >
                  <ChevronRight size={20} className="sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Thumbnail Navigation - Scrollable on mobile */}
              <div className="flex gap-2 mt-3 sm:mt-4 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible">
                {galleryImages.map((img, idx) => (
                  <button
                    key={img.id || idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`flex-shrink-0 w-14 h-10 sm:w-16 sm:h-10 rounded overflow-hidden border-2 transition-all ${
                      idx === currentIndex 
                        ? 'opacity-100' 
                        : 'border-transparent opacity-50 hover:opacity-75'
                    }`}
                    style={idx === currentIndex ? { borderColor: primaryColor } : {}}
                  >
                    <img 
                      src={thumbnailImage(img.src)} 
                      alt={img.alt} 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Videos Tab Content */}
        {activeTab === 'videos' && (
          <div className="max-w-6xl mx-auto">
            {galleryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleryVideos.map((video, idx) => (
                  <VideoCard 
                    key={video.id || idx} 
                    video={video} 
                    onClick={() => setVideoModal(video)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Video size={64} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">{t('gallery.noVideos')}</p>
                <p className="text-gray-500 text-sm mt-2">{t('gallery.videosComingSoon')}</p>
              </div>
            )}
          </div>
        )}

        {/* Photo Lightbox */}
        {lightboxOpen && galleryImages.length > 0 && (
          <div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            >
              <X size={32} />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
              }}
              className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"
            >
              <ChevronLeft size={40} />
            </button>
            
            <img
              src={galleryImages[lightboxIndex]?.src}
              alt={galleryImages[lightboxIndex]?.alt || 'Gallery'}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
              }}
              className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"
            >
              <ChevronRight size={40} />
            </button>
            
            <div className="absolute bottom-4 left-0 right-0 text-center text-white">
              <p className="text-lg">{galleryImages[lightboxIndex]?.caption || ''}</p>
              <p className="text-sm text-gray-400">{lightboxIndex + 1} / {galleryImages.length}</p>
            </div>
          </div>
        )}

        {/* Video Modal */}
        {videoModal && (
          <VideoModal video={videoModal} onClose={() => setVideoModal(null)} />
        )}
      </div>
    </section>
  );
};
